import { useEffect, useMemo, useState } from 'react'

import cn from 'classnames'

import ChainToken from '@src/components/ChainToken'
import ActiveBoxIcon from '@src/components/icons/ActiveBox'
import BoxIcon from '@src/components/icons/Box'
import CheckIcon from '@src/components/icons/Check'
import CloseIcon from '@src/components/icons/Close'
import ExchangeIcon from '@src/components/icons/Exchange'
import LoadingIcon from '@src/components/icons/Loading'
import ProcessIcon from '@src/components/icons/Process'
import TimerIcon from '@src/components/icons/Timer'
import { ResultCode, StepNames } from '@src/enums'
import { FeatherOrderStatus } from '@src/pages/Exchange/components/FeatherOrderFlow/FeatherOrderStatus'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useConfigStore } from '@src/zustand_stores'

interface IFeatherProcessingSwapProps {
    onClose: () => void
    currentStep: StepNames
    orderId: string
    result?: {
        transactionETA?: number
        status: ResultCode
        sourceTxHash?: string
        destinationTxHash?: string
        message?: string
    }
    fullTradeTimeTimer: any
}

export default function FeatherProcessingSwap({
    onClose,
    currentStep,
    orderId,
    result = undefined,
    fullTradeTimeTimer,
}: IFeatherProcessingSwapProps) {
    const availableNetworks = useConfigStore(config => config.availableNetworks)
    const [showDetails, setShowDetails] = useState(false)

    // Access exchange data
    const { sellData, updateSellData, buyData, updateBuyData, destinationAddress } = useExchangeDataContext()

    // Reset form values when order is completed
    useEffect(() => {
        if (result && result.status === ResultCode.success) {
            updateSellData({ ...sellData, value: BigInt(0) })
            updateBuyData({ ...buyData, value: BigInt(0) })
        }
    }, [result?.status])

    // Map Feather steps to our internal status representation
    const mapStepToFeatherStatus = (step: StepNames): FeatherOrderStatus => {
        switch (step) {
            case StepNames.FEATHER_WAITING_FOR_DEPOSIT:
                return FeatherOrderStatus.AWAITING_DEPOSIT
            case StepNames.FEATHER_DEPOSIT_DETECTED:
                return FeatherOrderStatus.DEPOSIT_CONFIRMED
            case StepNames.FEATHER_PROCESSING:
                // This can represent both PROCESSING and TRADING states
                return FeatherOrderStatus.TRADING
            case StepNames.FEATHER_SENDING_TO_DESTINATION:
                return FeatherOrderStatus.WITHDRAWING
            case StepNames.FEATHER_COMPLETED:
                return FeatherOrderStatus.COMPLETED
            case StepNames.FEATHER_EXPIRED:
                return FeatherOrderStatus.DEPOSIT_TIMEOUT
            case StepNames.FEATHER_FAILED:
                return FeatherOrderStatus.FAILED
            default:
                return FeatherOrderStatus.UNKNOWN
        }
    }

    // Make the component more resilient - add safety checks to handle missing data
    const safeStep = currentStep || StepNames.FEATHER_WAITING_FOR_DEPOSIT
    const safeResult = result || { status: ResultCode.pending }

    return (
        <div className='space-y-10 px-4'>
            <div className='flex w-full items-center'>
                <div className='flex-1' />
                <p className='flex-10 text-2xl font-medium'>Processing Feather Swap</p>
                <div className='flex flex-1 justify-end'>
                    <CloseIcon onClick={onClose} className='h-[18px] w-[18px] cursor-pointer text-mach-gray' />
                </div>
            </div>

            {/* Token exchange visualization */}
            <div className='relative flex h-[212px] items-center justify-between rounded-3xl border border-[#1A1B20] px-8'>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={sellData.token?.logoURI}
                        networkIcon={availableNetworks[sellData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
                <div className='absolute left-1/2 top-0 flex h-full -translate-x-1/2 items-center justify-between gap-2'>
                    <ProcessIcon isLeftIcon={true} />
                    <div className='flex h-5 w-5 items-center justify-center rounded-full bg-[#16171A]'>
                        <ExchangeIcon />
                    </div>
                    <ProcessIcon />
                </div>
                <div className='z-10 flex h-[104px] w-[104px] items-center justify-center rounded-full bg-[#161618]'>
                    <ChainToken
                        tokenLogo={buyData.token?.logoURI}
                        networkIcon={availableNetworks[buyData.chainId]?.icon}
                        tokenIconSize={50}
                        chainIconSize={20}
                    />
                </div>
            </div>

            {/* Status sections */}
            <div className='space-y-6'>
                {/* Timer section */}
                {safeResult.status === ResultCode.pending && (
                    <div className='flex items-center justify-center gap-2'>
                        <div className='flex items-center gap-1'>
                            <TimerIcon />
                        </div>
                        <span className='text-sm font-medium'>
                            {fullTradeTimeTimer.hours}h {fullTradeTimeTimer.minutes}m {fullTradeTimeTimer.seconds}s
                        </span>
                    </div>
                )}

                {/* Status cards */}
                {safeStep === StepNames.FEATHER_WAITING_FOR_DEPOSIT && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Waiting for Deposit</span>
                        </div>
                        <span className='text-[13px]'>
                            {safeResult.message ||
                                'Please send funds to the provided deposit address to begin the transfer.'}
                        </span>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_DEPOSIT_DETECTED && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Deposit Detected</span>
                        </div>
                        <span className='text-[13px]'>
                            {safeResult.message || 'Your deposit has been detected. Processing will begin shortly.'}
                        </span>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_PROCESSING && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Processing Exchange</span>
                        </div>
                        <span className='text-[13px]'>
                            {safeResult.message || 'Your exchange is being processed. This may take a few minutes.'}
                        </span>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_SENDING_TO_DESTINATION && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#4DB0F11A] px-3 py-2 text-[#4DB0F1]'>
                        <div className='flex items-center gap-2'>
                            <LoadingIcon className='h-[14px] w-[14px] animate-spin' />
                            <span className='text-sm font-medium'>Sending to Destination</span>
                        </div>
                        <div className='flex flex-col gap-1 text-[13px]'>
                            <span>
                                {safeResult.message ||
                                    'Exchange complete! Funds are being sent to your destination wallet.'}
                            </span>
                            {safeResult.destinationTxHash && (
                                <a
                                    href={`https://www.blockchain.com/explorer/transactions/btc/${
                                        safeResult.destinationTxHash.includes('/')
                                            ? safeResult.destinationTxHash.split('/').pop()
                                            : safeResult.destinationTxHash
                                    }`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-1 inline-flex items-center text-[13px] text-blue-400 hover:text-blue-300'
                                >
                                    <span className='underline'>View Transaction</span>
                                    <svg className='ml-1 h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                        />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_FAILED && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#FF39391A] px-3 py-2 text-[#FF3939]'>
                        <div className='flex items-center gap-2'>
                            <svg
                                width='14'
                                height='14'
                                viewBox='0 0 14 14'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z'
                                    fill='#FF3939'
                                    fillOpacity='0.1'
                                />
                                <path
                                    d='M7 3C7.55228 3 8 3.44772 8 4V7C8 7.55228 7.55228 8 7 8C6.44772 8 6 7.55228 6 7V4C6 3.44772 6.44772 3 7 3Z'
                                    fill='#FF3939'
                                />
                                <path
                                    d='M7 9C7.55228 9 8 9.44772 8 10C8 10.5523 7.55228 11 7 11C6.44772 11 6 10.5523 6 10C6 9.44772 6.44772 9 7 9Z'
                                    fill='#FF3939'
                                />
                            </svg>
                            <span className='text-sm font-medium'>Order Failed</span>
                        </div>
                        <span className='text-[13px]'>
                            {safeResult.message?.includes('Discord') ? (
                                <div className='space-y-2'>
                                    <p>{safeResult.message}</p>
                                    <div className='mt-1 rounded-md bg-[#FF39391A] p-2 font-mono text-xs'>
                                        Order ID: {orderId}
                                    </div>
                                </div>
                            ) : (
                                safeResult.message ||
                                'There was a problem processing your order. Please contact support for assistance.'
                            )}
                        </span>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_EXPIRED && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#FFA5001A] px-3 py-2 text-[#FFA500]'>
                        <div className='flex items-center gap-2'>
                            <svg
                                width='14'
                                height='14'
                                viewBox='0 0 14 14'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z'
                                    fill='#FFA500'
                                    fillOpacity='0.1'
                                />
                                <path
                                    d='M7 3C7.55228 3 8 3.44772 8 4V7C8 7.55228 7.55228 8 7 8C6.44772 8 6 7.55228 6 7V4C6 3.44772 6.44772 3 7 3Z'
                                    fill='#FFA500'
                                />
                                <path
                                    d='M7 9C7.55228 9 8 9.44772 8 10C8 10.5523 7.55228 11 7 11C6.44772 11 6 10.5523 6 10C6 9.44772 6.44772 9 7 9Z'
                                    fill='#FFA500'
                                />
                            </svg>
                            <span className='text-sm font-medium'>Order Expired</span>
                        </div>
                        <span className='text-[13px]'>
                            {safeResult.message || 'Your order has expired. Please create a new order.'}
                        </span>
                    </div>
                )}

                {safeStep === StepNames.FEATHER_COMPLETED && (
                    <div className='flex flex-col gap-2 rounded-lg bg-[#34C7591A] px-3 py-2 text-[#34C759]'>
                        <div className='flex items-center gap-2'>
                            <svg
                                width='14'
                                height='14'
                                viewBox='0 0 14 14'
                                fill='none'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path
                                    d='M7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z'
                                    fill='#34C759'
                                    fillOpacity='0.1'
                                />
                                <path
                                    d='M10.207 4.793C10.5976 5.18353 10.5976 5.81647 10.207 6.207L6.707 9.707C6.31653 10.0976 5.68347 10.0976 5.293 9.707L3.793 8.207C3.40247 7.81647 3.40247 7.18353 3.793 6.793C4.18353 6.40247 4.81647 6.40247 5.207 6.793L6 7.586L8.793 4.793C9.18353 4.40247 9.81647 4.40247 10.207 4.793Z'
                                    fill='#34C759'
                                />
                            </svg>
                            <span className='text-sm font-medium'>Order Complete</span>
                        </div>
                        <div className='flex flex-col gap-1 text-[13px]'>
                            <span>
                                {safeResult.message ||
                                    'Your exchange has been completed successfully! Funds have been sent to your destination wallet.'}
                            </span>
                            {safeResult.destinationTxHash && (
                                <a
                                    href={`https://www.blockchain.com/explorer/transactions/btc/${
                                        safeResult.destinationTxHash.includes('/')
                                            ? safeResult.destinationTxHash.split('/').pop()
                                            : safeResult.destinationTxHash
                                    }`}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='mt-1 inline-flex items-center text-[13px] text-green-400 hover:text-green-300'
                                >
                                    <span className='underline'>View Transaction</span>
                                    <svg className='ml-1 h-3 w-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                                        <path
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                            strokeWidth={2}
                                            d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                        />
                                    </svg>
                                </a>
                            )}
                        </div>
                    </div>
                )}

                {/* Progress steps visualization */}
                <div className='flex gap-2'>
                    <div className='flex flex-col items-center'>
                        <div className='flex h-8 w-8 items-center justify-center'>
                            <div
                                className={cn(
                                    'flex h-6 w-6 items-center justify-center rounded-full',
                                    safeStep !== StepNames.FEATHER_WAITING_FOR_DEPOSIT
                                        ? 'bg-[#34C759]'
                                        : 'bg-[#16171A] shadow-[0_0_2px_#349FFD]'
                                )}
                            >
                                {safeStep !== StepNames.FEATHER_WAITING_FOR_DEPOSIT ? (
                                    <CheckIcon width={12} height={12} />
                                ) : (
                                    <ActiveBoxIcon />
                                )}
                            </div>
                        </div>
                        <div className='h-12 w-[2px] bg-[#16171A]' />
                        <div className='flex h-8 w-8 items-center justify-center'>
                            <div
                                className={cn(
                                    'flex h-6 w-6 items-center justify-center rounded-full',
                                    [
                                        StepNames.FEATHER_PROCESSING,
                                        StepNames.FEATHER_SENDING_TO_DESTINATION,
                                        StepNames.FEATHER_COMPLETED,
                                    ].includes(safeStep)
                                        ? 'bg-[#34C759]'
                                        : safeStep === StepNames.FEATHER_DEPOSIT_DETECTED
                                          ? 'bg-[#16171A] shadow-[0_0_2px_#349FFD]'
                                          : 'bg-[#16171A]'
                                )}
                            >
                                {[
                                    StepNames.FEATHER_PROCESSING,
                                    StepNames.FEATHER_SENDING_TO_DESTINATION,
                                    StepNames.FEATHER_COMPLETED,
                                ].includes(safeStep) ? (
                                    <CheckIcon width={12} height={12} />
                                ) : safeStep === StepNames.FEATHER_DEPOSIT_DETECTED ? (
                                    <ActiveBoxIcon />
                                ) : (
                                    <BoxIcon />
                                )}
                            </div>
                        </div>
                        <div className='h-12 w-[2px] bg-[#16171A]' />
                        <div className='flex h-8 w-8 items-center justify-center'>
                            <div
                                className={cn(
                                    'flex h-6 w-6 items-center justify-center rounded-full',
                                    [StepNames.FEATHER_SENDING_TO_DESTINATION, StepNames.FEATHER_COMPLETED].includes(
                                        safeStep
                                    )
                                        ? 'bg-[#34C759]'
                                        : safeStep === StepNames.FEATHER_PROCESSING
                                          ? 'bg-[#16171A] shadow-[0_0_2px_#349FFD]'
                                          : 'bg-[#16171A]'
                                )}
                            >
                                {[StepNames.FEATHER_SENDING_TO_DESTINATION, StepNames.FEATHER_COMPLETED].includes(
                                    safeStep
                                ) ? (
                                    <CheckIcon width={12} height={12} />
                                ) : safeStep === StepNames.FEATHER_PROCESSING ? (
                                    <ActiveBoxIcon />
                                ) : (
                                    <BoxIcon />
                                )}
                            </div>
                        </div>
                        <div className='h-12 w-[2px] bg-[#16171A]' />
                        <div className='flex h-8 w-8 items-center justify-center'>
                            <div
                                className={cn(
                                    'flex h-6 w-6 items-center justify-center rounded-full',
                                    safeStep === StepNames.FEATHER_COMPLETED
                                        ? 'bg-[#34C759]'
                                        : safeStep === StepNames.FEATHER_SENDING_TO_DESTINATION
                                          ? 'bg-[#16171A] shadow-[0_0_2px_#349FFD]'
                                          : 'bg-[#16171A]'
                                )}
                            >
                                {safeStep === StepNames.FEATHER_COMPLETED ? (
                                    <CheckIcon width={12} height={12} />
                                ) : safeStep === StepNames.FEATHER_SENDING_TO_DESTINATION ? (
                                    <ActiveBoxIcon />
                                ) : (
                                    <BoxIcon />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className='flex flex-grow flex-col justify-between py-2'>
                        <div className='flex items-center justify-between'>
                            <span className='text-sm'>Deposit</span>
                            {safeStep === StepNames.FEATHER_WAITING_FOR_DEPOSIT ? (
                                <LoadingIcon className='animate-spin' />
                            ) : (
                                <CheckIcon width={20} height={20} />
                            )}
                        </div>
                        <div className='flex items-center justify-between text-sm'>
                            <span>Processing Deposit</span>
                            {safeStep === StepNames.FEATHER_DEPOSIT_DETECTED ? (
                                <LoadingIcon className='animate-spin' />
                            ) : [
                                  StepNames.FEATHER_PROCESSING,
                                  StepNames.FEATHER_SENDING_TO_DESTINATION,
                                  StepNames.FEATHER_COMPLETED,
                              ].includes(safeStep) ? (
                                <CheckIcon width={20} height={20} />
                            ) : (
                                ''
                            )}
                        </div>
                        <div className='flex items-center justify-between text-sm'>
                            <span>Processing Exchange</span>
                            {safeStep === StepNames.FEATHER_PROCESSING ? (
                                <LoadingIcon className='animate-spin' />
                            ) : [StepNames.FEATHER_SENDING_TO_DESTINATION, StepNames.FEATHER_COMPLETED].includes(
                                  safeStep
                              ) ? (
                                <CheckIcon width={20} height={20} />
                            ) : (
                                ''
                            )}
                        </div>
                        <div className='flex items-center justify-between text-sm'>
                            <span>Transfer Complete</span>
                            {safeStep === StepNames.FEATHER_SENDING_TO_DESTINATION ? (
                                <LoadingIcon className='animate-spin' />
                            ) : safeStep === StepNames.FEATHER_COMPLETED ? (
                                <CheckIcon width={20} height={20} />
                            ) : (
                                ''
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
